import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { ChatMessageActions } from '@/components/chatbot/chat-message-actions';
import { MemoizedReactMarkdown } from '@/components/chatbot/markdown';
import { CodeBlock } from '@/components/chatbot/codeblock';
import { IconSparkles } from '@/components/chatbot/icons';
import cn from 'mxcn';
import { Message } from '@baseai/core';
import { visit } from 'unist-util-visit';
import { CustomStylesI } from '.';
import { useIsDarkMode } from './hooks/use-is-dark-mode';

function rehypeInlineCodeProperty() {
	return function (tree: any) {
		visit(tree, 'element', function (node, index, parent) {
			if (node.tagName === 'code') {
				const isInline = !(parent && parent.tagName === 'pre');
				node.properties.inline = isInline.toString();
			}
		});
	};
}

export interface ChatMessageProps {
	message: Message;
	isLoading: boolean;
	customStyles?: CustomStylesI;
	enableActions?: boolean;
}

export function ChatMessage({
	message,
	isLoading,
	customStyles,
	enableActions = true,
	...props
}: ChatMessageProps) {
	const isDarkMode = useIsDarkMode();

	return (
		<div
			className={cn('lb-group lb-relative lb-flex lb-items-start')}
			{...props}
		>
			<div
				className={cn(
					'lb-flex lb-size-8 lb-select-none lb-items-center lb-justify-center lb-rounded-xl lb-border lb-border-solid lb-border-muted-foreground/40 lb-shadow',
					message.role === 'user'
						? 'lb-bg-background'
						: 'lb-bg-primary lb-text-primary-foreground'
				)}
				style={{
					...(customStyles?.chatbot?.foregroundColor && {
						backgroundColor:
							!isDarkMode && message.role === 'user'
								? customStyles?.chatbot?.foregroundColor
								: customStyles?.chatbot?.backgroundColor,
						color:
							!isDarkMode && message.role === 'user'
								? customStyles?.chatbot?.backgroundColor
								: customStyles?.chatbot?.foregroundColor
					})
				}}
			>
				{message.role === 'user' ? <>U</> : <IconSparkles />}
			</div>
			<div
				className="lb-relative lb-ml-4 lb-flex-1 lb-overflow-hidden lb-px-1"
				id="langbase-chatbot-content"
				style={{
					...(customStyles?.chatbot?.foregroundColor && {
						color: !isDarkMode
							? customStyles?.chatbot?.foregroundColor
							: customStyles?.chatbot?.backgroundColor
					})
				}}
			>
				<MemoizedReactMarkdown
					className={cn(
						'lb-prose lb-break-words lb-rounded-xl dark:lb-prose-invert lb-prose-p:leading-relaxed lb-prose-pre:rounded-xl lb-prose-pre:p-0 lb-text-inherit'
					)}
					remarkPlugins={[remarkGfm, remarkMath]}
					rehypePlugins={[rehypeInlineCodeProperty]}
					components={{
						p({ children }) {
							return (
								<p className="lb-mb-4 last:lb-mb-0">
									{children}
								</p>
							);
						},
						code({ node, className, children, ...props }) {
							const match = /language-(\w+)/.exec(
								className || ''
							);
							const isInline = node?.properties.inline === 'true';

							if (!isInline) {
								return (
									<CodeBlock
										key={Math.random()}
										language={(match && match[1]) || ''}
										value={String(children).replace(
											/\n$/,
											''
										)}
										{...props}
									/>
								);
							}

							return (
								<code className={className} {...props}>
									{children}
								</code>
							);
						}
					}}
				>
					{message.content || ''}
				</MemoizedReactMarkdown>
				{message.role !== 'user' && enableActions && (
					<ChatMessageActions isLoading message={message} />
				)}
			</div>
		</div>
	);
}
